import { useRegisterAgentContext } from '@backrs_ui/grow-ui/src/agentContext/useRegisterAgentContext';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { type CoachingResponse } from '@backrs_ui/grow-ui/src/redux/coachingAnalytics';

const { coachingAnalytics } = appStrings.modules;

export const useAgentContext = (data: CoachingResponse, detail = false) => {
  useRegisterAgentContext({
    kind: 'section',
    title: coachingAnalytics.coaching.title,
    description: coachingAnalytics.coaching.description,
  });

  useRegisterAgentContext({
    kind: 'visual',
    label: coachingAnalytics.coaching.load.title,
    display:
      'One card tabbed between institutional and Backrs coaches, showing average caseload, students with that coach type, and per-coach caseload bars.',
    detail: [
      coachingAnalytics.coaching.load.info,
      "students_with_coach counts students assigned to any coach of that type, including coaches not on this institution's roster, so it can differ from assignments, which sums the listed coaches' caseloads.",
    ].join('\n\n'),
    data: {
      coach_counts: data.coach_counts,
      by_type: summariseCoachTypes(data),
      coaches: [...data.by_type.backrs.coaches, ...data.by_type.institutional.coaches].map(
        (coach) => ({
          name: `${coach.first_name ?? ''} ${coach.last_name ?? ''}`.trim(),
          type: coach.type,
          caseload: coach.caseload,
          total_caseload: coach.total_caseload,
        }),
      ),
    },
  });

  const coaches = data.institutional_coaches;

  useRegisterAgentContext(
    detail
      ? {
          kind: 'visual',
          label: coachingAnalytics.coaching.coachRecency.title,
          display:
            "Two cards about the institution's own coaches only (Backrs coaches excluded): a login-recency donut and a monthly line chart of their feed posts and comments.",
          detail: [
            coachingAnalytics.coaching.coachRecency.info,
            coachingAnalytics.coaching.coachActivity.info,
          ].join('\n\n'),
          data: {
            institutional_coaches: coaches.total,
            recently_active: coaches.recently_active.buckets,
            activity_by_month: coaches.activity.monthly.series.map((month) => ({
              month: month.start,
              posts: month.posts,
              comments: month.comments,
            })),
          },
        }
      : null,
  );
};

const summariseCoachTypes = (coaching: CoachingResponse) => ({
  backrs: {
    coach_count: coaching.by_type.backrs.coach_count,
    assignments: coaching.by_type.backrs.assignments,
    average_per_coach: coaching.by_type.backrs.average_per_coach,
    students_with_coach: coaching.by_type.backrs.students_with_coach,
  },
  institutional: {
    coach_count: coaching.by_type.institutional.coach_count,
    assignments: coaching.by_type.institutional.assignments,
    average_per_coach: coaching.by_type.institutional.average_per_coach,
    students_with_coach: coaching.by_type.institutional.students_with_coach,
  },
});
